import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Building Rational Strength and Resilience',
    content: [
      '<strong>Overview:</strong> This module explores how to think clearly and act wisely under stress–strengthening rational wellness and decision-making.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Rational thinking vs. emotional impulsivity',
      '• Decision-making under pressure',
      '• Developing self-control and composure',
      '<br/>',
      '<strong>Discussion:</strong> Your rational mind is your internal compass–it helps you stay grounded when emotions surge.'
    ]
  },
  {
    title: 'Rational Thinking vs. Emotional Impulsivity',
    content: [
      'Rational thinking involves using logic, evidence, and long-term perspective to guide your decisions, rather than being driven by immediate emotional reactions.',
      '<br/>',
      '<strong>Rational Thinking Characteristics:</strong>',
      '• <strong>Evidence-Based:</strong> Decisions based on facts and data, not assumptions',
      '• <strong>Long-Term Perspective:</strong> Considers consequences beyond the immediate moment',
      '• <strong>Balanced:</strong> Weighs multiple factors and perspectives',
      '• <strong>Flexible:</strong> Open to new information and willing to adjust',
      '<br/>',
      '<strong>Emotional Impulsivity Signs:</strong>',
      '• Making decisions based on "heat of the moment" feelings',
      '• Regretting choices made when emotions were high',
      '• Difficulty delaying gratification',
      '• Reacting defensively to feedback or criticism',
      '<br/>',
      '<strong>The Balance:</strong> Emotions provide valuable information, but rational thinking ensures wise action. The goal is emotional awareness combined with rational control.'
    ]
  },
  {
    title: 'Decision-Making Under Pressure',
    content: [
      'High-pressure situations test your rational strength. Here are strategies for maintaining clarity when stress is high:',
      '<br/>',
      '<strong>Pause–Reflect–Respond Model:</strong>',
      '• <strong>Pause:</strong> Stop the automatic reaction. Take deep breaths.',
      '• <strong>Reflect:</strong> Ask yourself: "What\'s really happening? What are my options? What are the consequences?"',
      '• <strong>Respond:</strong> Choose your action based on rational assessment, not emotional impulse.',
      '<br/>',
      '<strong>Fact Checking Thoughts:</strong> When under pressure, ask:',
      '• <strong>Is it true?</strong> Is this thought based on facts or assumptions?',
      '• <strong>Is it helpful?</strong> Does this thought help me solve the problem?',
      '• <strong>Is it necessary?</strong> Do I need to focus on this right now?',
      '<br/>',
      '<strong>Values-Based Decision Making:</strong> Align your choices with what truly matters to you. Ask: "Does this action reflect my core values? Will I be proud of this decision tomorrow?"'
    ]
  },
  {
    title: 'Developing Self-Control and Composure',
    content: [
      'Self-control and composure are skills that can be developed through consistent practice. They form the foundation of rational resilience.',
      '<br/>',
      '<strong>Building Self-Control:</strong>',
      '• <strong>Start Small:</strong> Practice delaying small gratifications to build willpower',
      '• <strong>Manage Triggers:</strong> Identify situations that reduce your self-control and plan ahead',
      '• <strong>Use Implementation Intentions:</strong> "If X happens, then I will do Y"',
      '• <strong>Practice Mindfulness:</strong> Regular meditation builds awareness and control',
      '<br/>',
      '<strong>Maintaining Composure:</strong>',
      '• <strong>Accept Initial Reactions:</strong> Emotions are normal; you control your responses',
      '• <strong>Use Calming Techniques:</strong> Breathing exercises, progressive relaxation',
      '• <strong>Take Perspective:</strong> Ask "How important will this be in a week/month/year?"',
      '• <strong>Focus on Solutions:</strong> Direct energy toward what you can control',
      '<br/>',
      '<strong>Practical Activity:</strong> Identify one recurring situation where you tend to overreact emotionally. Write how a more rational approach could look next time.',
      '<br/>',
      '<strong>Example:</strong>',
      '• <strong>Situation:</strong> Receiving critical feedback',
      '• <strong>Emotional Reaction:</strong> Become defensive and argumentative',
      '• <strong>Rational Approach:</strong> Pause, thank them for feedback, ask for specifics, consider valid points'
    ],
    additionalSection: {
      title: 'Takeaway',
      content: [
        'Rational strength means clarity–thinking before reacting and acting with purpose. It\'s not about suppressing emotions but about giving your rational mind time to provide wise guidance. With practice, you can develop the composure to handle life\'s challenges with grace and effectiveness.',
        '<br/>',
        '<strong>Remember:</strong> Your rational mind is your greatest ally. It helps you transform emotional energy into purposeful action.'
      ]
    }
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 4: Building Rational Strength and Resilience! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about rational thinking and decision-making.',
        'The quiz will help you review key concepts about maintaining composure and making wise choices under pressure.'
      ]
    }
  }
];

const EmotioPsychoRationalModule4Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('emotioPsychoRationalM4');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/emotio-psycho-rational-module-4/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/emotio-psycho-rational-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div 
          className="module-left emotio-psycho-rational-panel"
        >
          <div className="module-header">
            <h1>Module 4</h1>
            <p className="module-subtitle">Building Rational Strength and Resilience</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Think clearly and act wisely under stress – strengthening rational wellness and decision-making.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content"> 
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title"> 
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            
            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/emotio-psycho-rational-module-4/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotioPsychoRationalModule4Page;


